import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import ProductGrid from '../components/products/ProductGrid';
import CategoryFilter from '../components/products/CategoryFilter';
import { products, categories } from '../data/products';

const Products = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState(
    searchParams.get('category') || 'all'
  );
  const [sortBy, setSortBy] = useState('name');
  const [filteredProducts, setFilteredProducts] = useState(products);
  
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  useEffect(() => {
    // Filter products based on category
    let filtered = selectedCategory === 'all' 
      ? products 
      : products.filter(product => product.category === selectedCategory);

    // Sort products
    filtered = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'price-low':
          return a.price - b.price;
        case 'price-high':
          return b.price - a.price;
        case 'rating':
          return b.rating - a.rating;
        case 'name':
        default:
          return a.name.localeCompare(b.name);
      }
    });

    setFilteredProducts(filtered);
  }, [selectedCategory, sortBy]);

  useEffect(() => {
    // Update URL when category changes
    if (selectedCategory === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ category: selectedCategory });
    }
  }, [selectedCategory, setSearchParams]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  const sortOptions = [
    { value: 'name', label: 'Name A-Z' },
    { value: 'price-low', label: 'Price: Low to High' },
    { value: 'price-high', label: 'Price: High to Low' },
    { value: 'rating', label: 'Highest Rated' }
  ];

  return (
    <div className="min-h-screen bg-dark-950 pt-20">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-dark relative overflow-hidden">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="hero-text text-white mb-6">
              Our <span className="text-gradient">Collection</span>
            </h1>
            <p className="text-xl text-dark-200 max-w-2xl mx-auto leading-relaxed">
              Discover our complete range of handcrafted resin furniture. Each piece is unique, 
              combining natural wood beauty with stunning epoxy artistry.
            </p>
          </motion.div>
        </div>
        
        {/* Background Elements */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-accent-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-accent-600/10 rounded-full blur-3xl"></div>
      </section>

      {/* Products Section */}
      <section className="py-16">
        <div className="container-custom">
          <motion.div
            ref={ref}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            variants={containerVariants}
          >
            {/* Filters and Sorting */}
            <motion.div variants={itemVariants} className="mb-12">
              <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-6 lg:space-y-0">
                {/* Category Filter */}
                <CategoryFilter
                  categories={categories}
                  selectedCategory={selectedCategory}
                  onCategoryChange={setSelectedCategory}
                />

                {/* Sort and Results Info */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-6">
                  {/* Results Count */}
                  <div className="text-dark-300">
                    Showing <span className="text-white font-semibold">{filteredProducts.length}</span> of{' '}
                    <span className="text-white font-semibold">{products.length}</span> products
                  </div>

                  {/* Sort Dropdown */}
                  <div className="flex items-center space-x-3">
                    <label className="text-dark-300 text-sm font-medium">Sort by:</label>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="bg-dark-800 border border-dark-600 text-white px-4 py-2 rounded-lg focus:border-accent-500 focus:outline-none"
                    >
                      {sortOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Products Grid */}
            <motion.div variants={itemVariants}>
              <ProductGrid products={filteredProducts} />
            </motion.div>

            {/* No Results */}
            {filteredProducts.length === 0 && (
              <motion.div
                variants={itemVariants}
                className="text-center py-16"
              >
                <div className="w-24 h-24 bg-dark-800 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-3xl">🔍</span>
                </div>
                <h3 className="text-2xl font-semibold text-white mb-4">
                  No products found
                </h3>
                <p className="text-dark-300 mb-6">
                  Try adjusting your filters or browse all products.
                </p>
                <button
                  onClick={() => setSelectedCategory('all')}
                  className="btn-primary"
                >
                  View All Products
                </button>
              </motion.div>
            )}

            {/* Load More / Pagination could go here */}
            {filteredProducts.length > 0 && (
              <motion.div variants={itemVariants} className="text-center mt-12">
                <div className="inline-flex items-center space-x-4 text-dark-300">
                  <div className="h-px bg-dark-700 w-16"></div>
                  <span>End of collection</span>
                  <div className="h-px bg-dark-700 w-16"></div>
                </div>
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Products;